import { useState, useCallback } from "react";
import * as api from "../components/DseChatComponent/chatListApi";
import type {
  TicketDraftResponse,
  ClarifyingQuestion,
  TicketDraft,
  SourceReference,
  AzureTicketRequest,
} from "../types/dse-knowledge";
import { notification } from "antd";

export type ChatState =
  | "idle"
  | "loading"
  | "ticket_prompt"
  | "ticket_description"
  | "ticket_questions"
  | "ticket_review"
  | "error";

export interface Message {
  id: string;
  type: "user" | "bot" | "error" | "ticket_success";
  content: string;
  confidence?: string;
  sources?: SourceReference[];
  timestamp: Date;
  /** URL of the created ticket — persisted here so it survives draft being cleared. */
  ticketUrl?: string;
}

export function useDSEChat(userEmail: string, userName?: string) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [chatState, setChatState] = useState<ChatState>("idle");
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [ticketSessionId, setTicketSessionId] = useState<string | null>(null);
  const [clarifyingQuestions, setClarifyingQuestions] = useState<ClarifyingQuestion[]>([]);
  const [draftPreview, setDraftPreview] = useState<string | null>(null);
  const [draft, setDraft] = useState<TicketDraft | null>(null);
  const [lastQuery, setLastQuery] = useState<string>("");
  const [lastResponse, setLastResponse] = useState<string>("");
  const [isSyncing, setIsSyncing] = useState(false);

  const addMessage = useCallback((msg: Omit<Message, "id" | "timestamp">) => {
    setMessages((prev) => [
      ...prev,
      { ...msg, id: crypto.randomUUID(), timestamp: new Date() },
    ]);
  }, []);

  // Centralized Ticket Response Handler
  const handleTicketResponse = useCallback((response: TicketDraftResponse) => {
    // Cast to any to bypass the "ready_for_review" comparability error
    switch (response.status as any) {
      case "gathering_info":
        setClarifyingQuestions(response.clarifying_questions || []);
        setChatState("ticket_questions");
        break;

      case "ready_for_review":
      case "draft_ready":
        setDraftPreview(response.draft_preview);
        setDraft(response.draft);
        setChatState("ticket_review");
        break;

      case "approved": // <--- ADD THIS CASE
        setDraftPreview(response.draft_preview);
        setDraft(response.draft);
        setChatState("ticket_review");
        break;

      case "submitted":
        addMessage({
          type: "bot",
          content: response.message,
          ticketUrl: response.draft?.url ?? undefined,
        });
        // --- THIS CLOSES THE TICKET UI ---
        setChatState("idle"); 
        setTicketSessionId(null);
        setDraft(null);
        setDraftPreview(null);
        setClarifyingQuestions([]);
        break;

      case "cancelled":
        addMessage({ type: "bot", content: "Ticket creation cancelled." });
        setChatState("idle");
        setTicketSessionId(null);
        setDraft(null);
        setDraftPreview(null);
        break;
        
      default:
        setChatState("idle");
    }
  }, [addMessage]);

  // Standard Chat logic
  const sendMessage = async (message: string) => {
    setLastQuery(message);
    addMessage({ type: "user", content: message });
    setChatState("loading");

    try {
      const response = await api.sendChat({
        message,
        user_email: userEmail,
        user_name: userName,
        session_id: sessionId || undefined,
      });

      setSessionId(response.session_id);
      setLastResponse(response.message);

      addMessage({
        type: "bot",
        content: response.message,
        confidence: response.confidence_level,
        sources: response.sources,
      });

      setChatState(response.requires_ticket_form ? "ticket_prompt" : "idle");
    } catch (error: any) {
      addMessage({ type: "error", content: error.message });
      setChatState("error");
    }
  };

  // Start ticket draft
  const handleStartTicket = async (description: string) => {
    setChatState("loading");
    try {
      const response = await api.startTicketDraft({
        original_query: lastQuery,
        original_response: lastResponse,
        description,
        requester_email: userEmail,
        requester_name: userName,
        session_id: sessionId || undefined,
      });
      setTicketSessionId(response.session_id);
      handleTicketResponse(response);
    } catch (error: any) {
      addMessage({ type: "error", content: error.message });
      setChatState("error");
    }
  };

  
  

  // Submit form answers
  const handleSubmitAnswers = async (answers: Record<string, string>) => {
    setChatState("loading");
    try {
      const response = await api.submitTicketAnswers({
        session_id: ticketSessionId!,
        answers,
      });
      handleTicketResponse(response);
    } catch (error: any) {
      addMessage({ type: "error", content: error.message });
      setChatState("error");
    }
  };

  // Final Action (Approve/Skip/Cancel)
  // const handleTicketAction = async (action: "approve" | "skip_questions" | "cancel") => {
  //   setChatState("loading");
  //   try {
  //     // Backend rejected 'approve_draft', using 'approve' directly
  //     const response = await api.ticketDraftAction({
  //       session_id: ticketSessionId!,
  //       action: action as any, 
  //     });
  //     handleTicketResponse(response);
  //   } catch (error: any) {
  //     addMessage({ type: "error", content: error.message });
  //     setChatState("error");
  //   }
  // };


    const handleAzureSubmit = async (session_id: string, currentDraft: any) => {
      setIsSyncing(true);
      
      const payload: AzureTicketRequest = {
        original_query: messages[0]?.content || "",
        original_response: messages[messages.length - 1]?.content || "",
        session_id: session_id,
        description: currentDraft?.description || "No description provided",
        additional_context: "User submitted via DSE Chat Component",
        screenshots: [],
        screenshot_descriptions: [],
        requester_email: userEmail,
        requester_name: userName ?? "",
        suggested_type: currentDraft?.ticket_type || "bug",
        urgency: "medium",
      };

      try {
        const response = await api.submitAzureTicket(payload);
        if (response.success) {
          notification.success({
            message: "Azure Sync Successful",
            description: `Work Item #${response.ticket_id} has been created.`,
            placement: "bottomRight",
          });
        }
        return response;
      } catch (error) {
        notification.error({
          message: "Azure Sync Failed",
          description: error instanceof Error ? error.message : "Internal Server Error",
        });
      } finally {
        setIsSyncing(false);
      }
    };

 
  // Inside useDSEChat.ts

const handleTicketAction = async (action: string) => {
  setChatState("loading");
  try {
    let response;
    
    // Switch between the generic action endpoint and the specific create endpoint
    if (action === "create_ticket") {
      response = await api.createTicket({ session_id: ticketSessionId! });
    } else {
      response = await api.ticketDraftAction({
        session_id: ticketSessionId!,
        action: action as any,
      });
    }
    
    handleTicketResponse(response);
  } catch (error: any) {
    addMessage({ type: "error", content: error.message });
    setChatState("error");
  }
};
  return {
    messages,
    chatState,
    clarifyingQuestions,
    draftPreview,
    draft,
    sendMessage,
    setChatState,
    handleStartTicket,
    handleSubmitAnswers,
    handleTicketAction,
    handleAzureSubmit,
    isSyncing,
    isLoading: chatState === "loading",
  };
}